import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { LoadingSpinner } from "@/components/shared/loading-spinner";
import { RequireAuth } from "@/components/auth/RequireAuth";
import { RequireClinicalMfa } from "@/components/auth/RequireClinicalMfa";
import { RequireRouteAccess } from "@/components/auth/RequireRouteAccess";
import { RequirePermission } from "@/components/auth/RequirePermission";
import { PracticeLayout } from "@/components/layout/PracticeLayout";
import { SettingsLayout } from "@/components/layout/SettingsLayout";
import PatientChartLayout from "@/components/layout/PatientChartLayout";
import EncounterLayout from "@/components/layout/EncounterLayout";
import AccountLayout from "@/components/layout/AccountLayout";
import MessagesStandaloneLayout from "@/components/layout/MessagesStandaloneLayout";
import TelehealthStandaloneLayout from "@/components/layout/TelehealthStandaloneLayout";
import { ChatControllerLayout } from "@/components/chat-widget/ChatControllerLayout";
import { PRESCRIBING_ROLES } from "@/lib/prescribing-access";
import { BreakGlassBanner } from "@/components/break-glass/break-glass-banner";

import { useAuth } from "@/hooks/use-auth";
import { isPrpAssigned, readLandingPage, resolveStaffLanding } from "@/lib/prp-home";
import { PRP_ONLY_HOME } from "@/lib/prp-only";
import { PRP_ENABLED } from "@/lib/hidden-features";
import { usePlanWorkspace } from "@/hooks/use-platform-billing";
import { PrpOnlyGuard } from "@/components/auth/PrpOnlyGuard";

// ---------- Lazy page imports ----------
const Login = lazy(() => import("@/pages/Login"));
const EntraCallback = lazy(() => import("@/pages/auth/EntraCallback"));
const MfaSetupOnboarding = lazy(() => import("@/pages/onboarding/MfaSetup"));
const SetPassword = lazy(() => import("@/pages/SetPassword"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const DeviceSecurity = lazy(() => import("@/pages/security/device"));
const IntakeFill = lazy(() => import("@/pages/intake/fill"));
const PrpSign = lazy(() => import("@/pages/prp-sign/sign-page"));
const OpenLetter = lazy(() => import("@/pages/letters/open-letter-page"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Patients = lazy(() => import("@/pages/patients/index"));
const PatientCreate = lazy(() => import("@/pages/patients/create"));
const PatientDetail = lazy(() => import("@/pages/patients/[id]/index"));
const PatientEncounters = lazy(
  () => import("@/pages/patients/[id]/encounters"),
);
const PatientAllergies = lazy(
  () => import("@/pages/patients/[id]/allergies"),
);
const PatientConditions = lazy(
  () => import("@/pages/patients/[id]/conditions"),
);
const PatientMedications = lazy(
  () => import("@/pages/patients/[id]/medications"),
);
const PatientPrescriptions = lazy(
  () => import("@/pages/patients/[id]/prescriptions"),
);
const NewRxPage = lazy(() => import("@/pages/patients/[id]/new-rx"));
const PatientVitals = lazy(() => import("@/pages/patients/[id]/vitals"));
const PatientDocuments = lazy(
  () => import("@/pages/patients/[id]/documents"),
);
const PatientImmunizations = lazy(
  () => import("@/pages/patients/[id]/immunizations"),
);
const PatientImplantableDevices = lazy(
  () => import("@/pages/patients/[id]/implantable-devices"),
);
const PatientSocialHistory = lazy(
  () => import("@/pages/patients/[id]/social-history"),
);
const PatientDemographics = lazy(
  () => import("@/pages/patients/[id]/demographics"),
);
const PatientCarePlans = lazy(
  () => import("@/pages/patients/[id]/care-plans"),
);
const PatientCarePlanDetail = lazy(
  () => import("@/pages/patients/[id]/care-plan-detail"),
);
const PatientCareTeam = lazy(
  () => import("@/pages/patients/[id]/care-team"),
);
const PatientRecall = lazy(
  () => import("@/pages/patients/[id]/recall"),
);
const PatientBilling = lazy(
  () => import("@/pages/patients/[id]/account"),
);
const PatientSettings = lazy(
  () => import("@/pages/patients/[id]/settings"),
);
const EncounterWorkspace = lazy(
  () => import("@/pages/encounters/[id]"),
);
const Schedule = lazy(() => import("@/pages/schedule/index"));
const BookAppointment = lazy(() => import("@/pages/schedule/book-appointment/index"));
const BookingRequests = lazy(() => import("@/pages/schedule/booking-requests/index"));
const PatientTracker = lazy(() => import("@/pages/patient-tracker/index"));
const TelehealthSession = lazy(() => import("@/pages/telehealth/[sessionId]"));
const TelehealthAppointment = lazy(() => import("@/pages/telehealth/appointment/[appointmentId]"));
const TelehealthRoom = lazy(() => import("@/pages/telehealth/room"));
const TelehealthEnded = lazy(() => import("@/pages/telehealth/ended/[sessionId]"));
const MessagesInbox = lazy(() => import("@/pages/messages/index"));
const MessageDetail = lazy(() => import("@/pages/messages/[id]"));
const ChatPage = lazy(() => import("@/pages/messages/chat/index"));
const ComposePage = lazy(() => import("@/pages/messages/compose/index"));
const PatientSMSPage = lazy(() => import("@/pages/messages/sms/index"));
const Prescriptions = lazy(() => import("@/pages/prescriptions/index"));
const PrescriptionDetail = lazy(() => import("@/pages/prescriptions/[id]"));
const HelpPage = lazy(() => import("@/pages/help/index"));
const RecordExchangeInbox = lazy(() => import("@/pages/record-exchange/index"));
const ReceivedDocuments = lazy(() => import("@/pages/received-documents/index"));
const ReceivedDocumentDetail = lazy(() => import("@/pages/received-documents/[id]"));
const FormsWorkspace = lazy(() => import("@/pages/forms/index"));
const TasksPage = lazy(() => import("@/pages/tasks/index"));
const OpenNotesPage = lazy(() => import("@/pages/open-notes/index"));
const PendingCosignsPage = lazy(() => import("@/pages/pending-cosigns/index"));
const FormBuilder = lazy(() => import("@/pages/forms/builder"));
const Billing = lazy(() => import("@/pages/billing/index"));
const Community = lazy(() => import("@/pages/community/index"));
const CommunityPostEditor = lazy(() => import("@/pages/community/post-editor"));
const CommunityEventEditor = lazy(() => import("@/pages/community/event-editor"));
const BillingClaims = lazy(() => import("@/pages/billing/claims"));
const BillingClearinghouse = lazy(() => import("@/pages/billing/clearinghouse"));
const BillingWorkspaceLayout = lazy(() => import("@/pages/billing/BillingWorkspaceLayout"));
const PrpWorkspaceLayout = lazy(() => import("@/pages/prp/PrpWorkspaceLayout"));
const PrpDashboard = lazy(() => import("@/pages/prp/dashboard"));
const PrpTasks = lazy(() => import("@/pages/prp/tasks"));
const PrpCaseload = lazy(() => import("@/pages/prp/caseload"));
const PrpDocuments = lazy(() => import("@/pages/prp/documents"));
const PrpReferrals = lazy(() => import("@/pages/prp/intakes"));
const PrpIntakeDetail = lazy(() => import("@/pages/prp/intake-detail"));
const PrpDocumentDetail = lazy(() => import("@/pages/prp/document-detail"));
const PrpCompliance = lazy(() => import("@/pages/prp/compliance"));
const PrpSignup = lazy(() => import("@/pages/signup/prp-signup"));
const PracticeSignup = lazy(() => import("@/pages/signup/practice-signup"));
const MyDay = lazy(() => import("@/pages/schedule/my-day"));
const PrpAuthorizations = lazy(() => import("@/pages/prp/authorizations"));
const PrpClientChartLayout = lazy(() => import("@/pages/prp/client-chart"));
const PrpClientTab = lazy(() => import("@/pages/prp/client-chart").then((m) => ({ default: m.PrpClientTab })));
const BillingEra = lazy(() => import("@/pages/billing/era"));
const BillingDenials = lazy(() => import("@/pages/billing/denials"));
const BillingAnalytics = lazy(() => import("@/pages/billing/analytics"));
const BillingEligibilityBatch = lazy(() => import("@/pages/billing/eligibility-batch"));
const BillingPriorAuth = lazy(() => import("@/pages/billing/prior-auth"));
const BillingEnrollments = lazy(() => import("@/pages/billing/enrollments"));
const BillingNewClaim = lazy(() => import("@/pages/billing/new-claim"));
const BillingEditClaim = lazy(() => import("@/pages/billing/edit-claim"));
const BillingClaimReview = lazy(() => import("@/pages/billing/claim-review"));
const BillingClaimRevSearch = lazy(() => import("@/pages/billing/claimrev-search"));
const BillingClaimWorkbenchClaim = lazy(() => import("@/pages/billing/claim-workbench-claim"));
const BillingInputFiles = lazy(() => import("@/pages/billing/input-files"));
const BillingPayers = lazy(() => import("@/pages/billing/payers"));
const BillingUnbilledVisits = lazy(() => import("@/pages/billing/unbilled-visits"));
const BillingBills = lazy(() => import("@/pages/billing/bills"));
const BillingBillDetail = lazy(() => import("@/pages/billing/bill-detail"));
const BillingPayments = lazy(() => import("@/pages/billing/payments"));
const BillingPaymentDetail = lazy(() => import("@/pages/billing/payment-detail"));
const BillingPriceList = lazy(() => import("@/pages/billing/price-list"));
const BillingAccounts = lazy(() => import("@/pages/billing/accounts"));
const BillingJournal = lazy(() => import("@/pages/billing/journal"));
const BillingApprovals = lazy(() => import("@/pages/billing/approvals"));
const BillingCashUp = lazy(() => import("@/pages/billing/cash-up"));
const BillingReports = lazy(() => import("@/pages/billing/reports"));
const BillingPeriods = lazy(() => import("@/pages/billing/periods"));
const BillingAccountLedger = lazy(() => import("@/pages/billing/account-ledger"));
const BillingOnline = lazy(() => import("@/pages/billing/online"));
const CorporateWorkspaceLayout = lazy(() => import("@/pages/corporate/CorporateWorkspaceLayout"));
const CorporateClients = lazy(() => import("@/pages/corporate/index"));
const CorporateClientNew = lazy(() => import("@/pages/corporate/new"));
const CorporateClientDetail = lazy(() => import("@/pages/corporate/[id]"));
const CorporateInvoice = lazy(() => import("@/pages/corporate/invoice"));
const CompanyInvoicePrint = lazy(() => import("@/pages/billing/print").then((m) => ({ default: m.CompanyInvoicePrint })));
const CompanyReportPrint = lazy(() => import("@/pages/billing/print").then((m) => ({ default: m.CompanyReportPrint })));
const BillingPayouts = lazy(() => import("@/pages/billing/payouts"));
const BillPrint = lazy(() => import("@/pages/billing/print").then((m) => ({ default: m.BillPrint })));
const ReceiptPrint = lazy(() => import("@/pages/billing/print").then((m) => ({ default: m.ReceiptPrint })));
const Reports = lazy(() => import("@/pages/reports/index"));
const DecisionSupport = lazy(
  () => import("@/pages/decision-support/index"),
);
const CAMOSManagement = lazy(() => import("@/pages/camos/index"));
const GroupsPage = lazy(() => import("@/pages/groups/index"));
// Settings module - each page lazy-loaded
const SettingsOverview      = lazy(() => import("@/pages/settings/index"));
const SettingsOrganization  = lazy(() => import("@/pages/settings/organization/index"));
const SettingsLocations     = lazy(() => import("@/pages/settings/locations/index"));
const SettingsDepartments   = lazy(() => import("@/pages/settings/departments/index"));
const SettingsDepartmentDetail = lazy(() => import("@/pages/settings/departments/detail"));
const SettingsServicesFees  = lazy(() => import("@/pages/settings/services-fees/index"));
const SettingsVisitReasons  = lazy(() => import("@/pages/settings/visit-reasons/index"));
const SettingsImportPatients = lazy(() => import("@/pages/settings/import-patients/index"));
const SettingsPublicSite    = lazy(() => import("@/pages/settings/public-site/index"));
const SettingsPublicSiteStudio = lazy(() => import("@/pages/settings/public-site/studio/website-studio"));
const SettingsUsers         = lazy(() => import("@/pages/settings/users/index"));
const SettingsUsersNew      = lazy(() => import("@/pages/settings/users/new"));
const SettingsUserDetail    = lazy(() => import("@/pages/settings/users/[id]/index"));
const SettingsRoles         = lazy(() => import("@/pages/settings/roles/index"));
const SettingsSecurity      = lazy(() => import("@/pages/settings/security/index"));
const SettingsPrpProgram    = lazy(() => import("@/pages/settings/prp/index"));
const SettingsAudit         = lazy(() => import("@/pages/settings/audit/index"));
const SettingsEmailOutbox   = lazy(() => import("@/pages/settings/email-outbox/index"));
const SettingsIntegrations  = lazy(() => import("@/pages/settings/integrations/index"));
const SettingsClaimRev      = lazy(() => import("@/pages/settings/integrations/claimrev"));
const SettingsStripeConnect = lazy(() => import("@/pages/settings/integrations/stripe"));
const SettingsEPrescribing  = lazy(() => import("@/pages/settings/integrations/eprescribing"));
const SettingsExchangePartners = lazy(() => import("@/pages/settings/integrations/exchange-partners"));
const SettingsPaystack = lazy(() => import("@/pages/settings/integrations/paystack"));
const SettingsUserPermissions = lazy(() => import("@/pages/settings/users/[id]/permissions"));
const SettingsProviders     = lazy(() => import("@/pages/settings/providers/index"));
const SettingsProviderDetail = lazy(() => import("@/pages/settings/providers/[id]/index"));
// User account settings
const AccountSettings = lazy(() => import("@/pages/account/index"));

// ---------- Landing Page Redirect ----------
function LandingRedirect() {
  const { user } = useAuth();
  const { prpOnly, isLoading } = usePlanWorkspace();
  if (isLoading) return null;
  if (prpOnly) return <Navigate to={PRP_ONLY_HOME} replace />;
  const dest = resolveStaffLanding({
    role: user?.role,
    canUsePrp: true,
    prpAssigned: isPrpAssigned(user),
    landingPage: readLandingPage(user?.preferences),
  });
  return <Navigate to={dest} replace />;
}

// ---------- Suspense fallback ----------
function SuspenseFallback() {
  return <LoadingSpinner fullPage message="Loading…" />;
}
export default function App() {
  return (
    <Suspense fallback={<SuspenseFallback />}>
      <BreakGlassBanner />
      <Routes>
        {/* Public */}
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<EntraCallback />} />
        <Route path="/onboarding/mfa" element={<MfaSetupOnboarding />} />
        <Route path="/auth/set-password" element={<SetPassword />} />
        <Route path="/auth/forgot-password" element={<ForgotPassword />} />
        <Route path="/security/device" element={<DeviceSecurity />} />
        <Route path="/intake/fill" element={<IntakeFill />} />
        <Route path="/sign/prp/:token" element={<PrpSign />} />
        <Route path="/signup" element={<PracticeSignup />} />
        <Route path="/signup/prp" element={PRP_ENABLED ? <PrpSignup /> : <Navigate to="/" replace />} />
        <Route path="/letters/:token" element={<OpenLetter />} />
        <Route path="/telehealth/room/:sessionId" element={<TelehealthRoom />} />
        <Route path="/telehealth/ended/:sessionId" element={<TelehealthEnded />} />

        {/* Protected - require auth */}
        <Route element={<RequireAuth />}>
        <Route element={<PrpOnlyGuard />}>
        <Route element={<ChatControllerLayout />}>
        <Route element={<PracticeLayout />}>
          <Route index element={<LandingRedirect />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="help" element={<HelpPage />} />

          {/* Schedule - all clinical roles */}
          <Route path="schedule" element={<Schedule />} />
          <Route path="schedule/book" element={<BookAppointment />} />
          <Route path="my-day" element={<MyDay />} />

          {/* Booking Requests inbox - provider + admins only (matches the
              public_booking_requests RBAC grant; the backend re-enforces
              provider-owns-request scoping regardless of this UI gate). */}
          <Route element={<RequireRouteAccess allowedRoles={["super_admin","org_admin","provider"]} />}>
            <Route path="schedule/booking-requests" element={<BookingRequests />} />
            {/* Community: articles, research and events for the Orange website */}
            <Route path="community" element={<Community />} />
            <Route path="community/posts/:id" element={<CommunityPostEditor />} />
            <Route path="community/events/:id" element={<CommunityEventEditor />} />
          </Route>

          {/* Messages - all roles */}
          <Route path="messages" element={<MessagesInbox />} />
          <Route path="messages/compose" element={<ComposePage />} />
          <Route path="messages/sms" element={<PatientSMSPage />} />
          <Route path="messages/:id" element={<MessageDetail />} />

          {/* Patient Tracker - all except billing */}
          <Route element={<RequireRouteAccess allowedRoles={["super_admin","org_admin","provider","physician","nurse","front_desk"]} />}>
            <Route path="tracker" element={<PatientTracker />} />
          </Route>

          {/* Patients list + create - all except billing, MFA required */}
          <Route element={<RequireRouteAccess allowedRoles={["super_admin","org_admin","provider","physician","nurse","front_desk"]} />}>
          <Route element={<RequireClinicalMfa />}>
            <Route path="patients" element={<Patients />} />
            <Route path="patients/new" element={<PatientCreate />} />
          </Route>
          </Route>

          {/* Prescriptions - provider + admins only */}
          <Route element={<RequireRouteAccess allowedRoles={["super_admin","org_admin","provider","physician"]} />}>
            <Route path="prescriptions" element={<Prescriptions />} />
            <Route path="prescriptions/:id" element={<PrescriptionDetail />} />
          </Route>

          {/* Record Exchange - provider + admins */}
          <Route element={<RequireRouteAccess allowedRoles={["super_admin","org_admin","provider"]} />}>
            <Route path="record-exchange" element={<RecordExchangeInbox />} />
            <Route path="received-documents" element={<ReceivedDocuments />} />
            <Route path="received-documents/:id" element={<ReceivedDocumentDetail />} />
          </Route>

          {/* Lab Orders - clinical roles */}
          <Route element={<RequireRouteAccess allowedRoles={["super_admin","org_admin","provider","physician","nurse"]} />}>
            {/* Labs are not offered on this platform. */}
            <Route path="lab-orders" element={<Navigate to="/" replace />} />
          </Route>

          {/* Forms & Intake - clinical and front-desk workflow */}
          <Route element={<RequireRouteAccess allowedRoles={["super_admin","org_admin","provider","physician","nurse","front_desk"]} />}>
            <Route path="forms" element={<FormsWorkspace />} />
            <Route path="forms/builder/new" element={<FormBuilder />} />
            <Route path="forms/builder/:id" element={<FormBuilder />} />
          </Route>

          <Route element={<RequireRouteAccess allowedRoles={["super_admin","org_admin","provider","physician","nurse","front_desk"]} />}>
            <Route path="tasks" element={<TasksPage />} />
            <Route path="open-notes" element={<OpenNotesPage />} />
            <Route path="pending-cosigns" element={<PendingCosignsPage />} />
          </Route>

          {/* Billing (subscription/account) - stays in the main app shell */}
          <Route element={<RequireRouteAccess allowedRoles={["super_admin","org_admin","billing_staff"]} />}>
            <Route path="billing" element={<Billing />} />
          </Route>

          {/* Reports - all except front_desk and nurse */}
          <Route element={<RequireRouteAccess allowedRoles={["super_admin","org_admin","provider","billing_staff"]} />}>
            <Route path="reports" element={<Reports />} />
          </Route>

          {/* Decision Support - clinical roles + admins */}
          <Route element={<RequireRouteAccess allowedRoles={["super_admin","org_admin","provider","physician","nurse"]} />}>
            <Route path="decision-support" element={<DecisionSupport />} />
          </Route>

          {/* Clinical Templates - clinical roles */}
          <Route element={<RequireRouteAccess allowedRoles={["super_admin","org_admin","provider","nurse"]} />}>
            <Route path="camos" element={<CAMOSManagement />} />
          </Route>

          {/* Therapy / PRP groups - by RBAC so PRP counselors can run PRP groups */}
          <Route element={<RequirePermission resource="groups" action="list" />}>
            <Route path="groups" element={<GroupsPage />} />
          </Route>

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>

          {/* ── Encounter Workspace - admins + clinical only, MFA required ── */}
          <Route element={<RequireRouteAccess allowedRoles={["super_admin","org_admin","provider","physician","nurse"]} />}>
          <Route element={<RequireClinicalMfa />}>
          <Route element={<EncounterLayout />}>
            <Route path="encounters/:id" element={<EncounterWorkspace />} />
          </Route>
          </Route>
          </Route>

          {/* ── Account / User Profile – all roles ── */}
          <Route path="account" element={<AccountLayout />}>
            <Route index element={<Navigate to="profile" replace />} />
            <Route path=":section" element={<AccountSettings />} />
          </Route>

          {/* ── Corporate clients (EAP): own sidebar, like Billing ── */}
          <Route element={<RequirePermission resource="corporate_clients" action="list" />}>
          <Route element={<CorporateWorkspaceLayout />}>
            <Route path="corporate" element={<CorporateClients />} />
            <Route path="corporate/new" element={<CorporateClientNew />} />
            <Route path="corporate/:id" element={<CorporateClientDetail />} />
            <Route element={<RequirePermission resource="billing" action="read" />}>
              <Route path="corporate/:id/invoices/:invoiceId" element={<CorporateInvoice />} />
            </Route>
          </Route>
          </Route>

          {/* ── Patient billing: bills, payments, price list, ledger. Gated by RBAC so the front desk can bill too. ── */}
          <Route element={<RequirePermission resource="billing" action="list" />}>
          <Route element={<BillingWorkspaceLayout />}>
            <Route path="billing/bills" element={<BillingBills />} />
            <Route path="billing/bills/new" element={<BillingBillDetail />} />
            <Route path="billing/bills/:id" element={<BillingBillDetail />} />
            <Route path="billing/payments" element={<BillingPayments />} />
            <Route path="billing/payments/:id" element={<BillingPaymentDetail />} />
            <Route path="billing/online" element={<BillingOnline />} />
            <Route path="billing/price-list" element={<BillingPriceList />} />
            <Route path="billing/approvals" element={<BillingApprovals />} />
            <Route path="billing/cash-up" element={<BillingCashUp />} />
            <Route path="billing/reports" element={<BillingReports />} />
            {/* The patient's account, for billing roles that have no access to the chart. */}
            <Route path="billing/patients/:id" element={<PatientBilling />} />
            <Route element={<RequirePermission resource="accounting" action="list" />}>
              <Route path="billing/accounts" element={<BillingAccounts />} />
              <Route path="billing/journal" element={<BillingJournal />} />
              <Route path="billing/accounts/:id" element={<BillingAccountLedger />} />
              <Route path="billing/periods" element={<BillingPeriods />} />
              <Route path="billing/payouts" element={<BillingPayouts />} />
            </Route>
          </Route>
          <Route path="billing/bills/:id/print" element={<BillPrint />} />
          <Route path="billing/payments/:id/print" element={<ReceiptPrint />} />
          <Route path="corporate/:id/invoices/:invoiceId/print" element={<CompanyInvoicePrint />} />
          <Route path="corporate/:id/report/print" element={<CompanyReportPrint />} />
          </Route>

          {/* ── Billing / Claims Workspace - own sidebar, shared header (billing + admins) ── */}
          <Route element={<RequireRouteAccess allowedRoles={["super_admin","org_admin","billing_staff"]} />}>
          <Route element={<BillingWorkspaceLayout />}>
            <Route path="billing/unbilled-visits" element={<BillingUnbilledVisits />} />
            {/* Same page as Settings → Services & Fees, reachable by billing staff. */}
            <Route path="billing/services-fees" element={<SettingsServicesFees />} />
            <Route path="billing/claims/new" element={<BillingNewClaim />} />
            <Route path="billing/claims/:id" element={<BillingClaimReview />} />
            <Route path="billing/claims/:id/edit" element={<BillingEditClaim />} />
            <Route path="billing/claimrev-search" element={<BillingClaimRevSearch />} />
            <Route path="billing/workbench/claims/:objectId" element={<BillingClaimWorkbenchClaim />} />
            <Route path="billing/claims" element={<BillingClaims />} />
            <Route path="billing/denials" element={<BillingDenials />} />
            <Route path="billing/input-files" element={<BillingInputFiles />} />
            <Route path="billing/payers" element={<BillingPayers />} />
            <Route path="billing/clearinghouse" element={<BillingClearinghouse />} />
            <Route path="billing/era" element={<BillingEra />} />
            <Route path="billing/analytics" element={<BillingAnalytics />} />
            <Route path="billing/eligibility-batch" element={<BillingEligibilityBatch />} />
            <Route path="billing/prior-auth" element={<BillingPriorAuth />} />
            <Route path="billing/enrollments" element={<BillingEnrollments />} />
          </Route>
          </Route>

          {/* ── PRP Workspace - own sidebar; gated by the "prp" RBAC resource ── */}
          <Route element={<RequirePermission resource="prp" action="list" />}>
          <Route element={<RequireClinicalMfa />}>
          <Route element={<PrpWorkspaceLayout />}>
            <Route path="prp" element={<PrpDashboard />} />
            <Route path="prp/caseload" element={<PrpCaseload />} />
            <Route path="prp/documents" element={<PrpDocuments kind="all" />} />
            <Route path="prp/contact-notes" element={<PrpDocuments kind="contact-notes" />} />
            <Route path="prp/referrals" element={<PrpReferrals />} />
            <Route path="prp/intakes/:id" element={<PrpIntakeDetail />} />
            <Route path="prp/irps" element={<PrpDocuments kind="irps" />} />
            <Route path="prp/dla20" element={<PrpDocuments kind="dla20" />} />
            <Route path="prp/monthly-summaries" element={<PrpDocuments kind="monthly-summaries" />} />
            <Route path="prp/reviews" element={<PrpDocuments kind="reviews" />} />
            <Route path="prp/authorizations" element={<PrpAuthorizations />} />
            <Route element={<RequirePermission resource="tasks" action="list" />}>
              <Route path="prp/tasks" element={<PrpTasks />} />
            </Route>
            <Route element={<RequirePermission resource="messages" action="list" />}>
              <Route path="prp/messages" element={<MessagesInbox />} />
              <Route path="prp/messages/compose" element={<ComposePage />} />
              <Route path="prp/messages/sms" element={<PatientSMSPage />} />
              <Route path="prp/messages/:id" element={<MessageDetail />} />
            </Route>
            <Route
              path="prp/compliance"
              element={<PrpCompliance />}
            />
            {/* Same page as Settings → PRP Program, for Program Directors (no Settings access). */}
            <Route path="prp/settings" element={<SettingsPrpProgram />} />
            <Route path="prp/documents/:id" element={<PrpDocumentDetail />} />
            <Route path="prp/clients/:patientId" element={<PrpClientChartLayout />}>
              <Route index element={<PrpClientTab tab="" />} />
              <Route path="referral" element={<PrpClientTab tab="referral" />} />
              <Route path="dla20" element={<PrpClientTab tab="dla20" />} />
              <Route path="presenting-problems" element={<PrpClientTab tab="presenting-problems" />} />
              <Route path="irp" element={<PrpClientTab tab="irp" />} />
              <Route path="contact-notes" element={<PrpClientTab tab="contact-notes" />} />
              <Route path="monthly-summaries" element={<PrpClientTab tab="monthly-summaries" />} />
              <Route path="discharge" element={<PrpClientTab tab="discharge" />} />
            </Route>
          </Route>
          </Route>
          </Route>

          {/* ── Settings - admins only ── */}
          <Route element={<RequireRouteAccess allowedRoles={["super_admin","org_admin"]} />}>
          <Route element={<SettingsLayout />}>
            <Route path="settings" element={<SettingsOverview />} />
            {/* The OpenEMR-style Config page saved nothing; hidden until it is wired to real settings. */}
            <Route path="settings/config" element={<Navigate to="/settings" replace />} />
            <Route path="settings/organization" element={<SettingsOrganization />} />
            <Route path="settings/locations" element={<SettingsLocations />} />
            <Route path="settings/departments" element={<SettingsDepartments />} />
            <Route path="settings/departments/:id" element={<SettingsDepartmentDetail />} />
            <Route path="settings/services-fees" element={<SettingsServicesFees />} />
            <Route path="settings/visit-reasons" element={<SettingsVisitReasons />} />
            <Route path="settings/import-patients" element={<SettingsImportPatients />} />
            <Route path="settings/public-site" element={<SettingsPublicSite />} />
            <Route path="settings/users" element={<SettingsUsers />} />
            <Route path="settings/users/new" element={<SettingsUsersNew />} />
            <Route path="settings/users/:id" element={<SettingsUserDetail />} />
            <Route path="settings/users/:id/permissions" element={<SettingsUserPermissions />} />
            <Route path="settings/providers" element={<SettingsProviders />} />
            <Route path="settings/providers/:id" element={<SettingsProviderDetail />} />
            <Route path="settings/roles" element={<SettingsRoles />} />
            <Route path="settings/security" element={<SettingsSecurity />} />
            <Route path="settings/prp" element={PRP_ENABLED ? <SettingsPrpProgram /> : <Navigate to="/settings" replace />} />
            <Route path="settings/audit" element={<SettingsAudit />} />
            <Route path="settings/email-outbox" element={<SettingsEmailOutbox />} />
            <Route path="settings/record-exchange" element={<RecordExchangeInbox />} />
            {/* Preferences / Notifications pages saved nothing; hidden until wired to real settings. */}
            <Route path="settings/preferences" element={<Navigate to="/settings" replace />} />
            <Route path="settings/notifications" element={<Navigate to="/settings" replace />} />
            <Route path="settings/integrations" element={<SettingsIntegrations />} />
            <Route path="settings/integrations/paystack" element={<SettingsPaystack />} />
            <Route path="settings/integrations/claimrev" element={<SettingsClaimRev />} />
            <Route path="settings/integrations/stripe" element={<SettingsStripeConnect />} />
            <Route path="settings/integrations/eprescribing" element={<SettingsEPrescribing />} />
            <Route path="settings/integrations/exchange-partners" element={<SettingsExchangePartners />} />
          </Route>
          </Route>

          {/* ── Patient Chart - all except billing, MFA required ── */}
          <Route element={<RequireRouteAccess allowedRoles={["super_admin","org_admin","provider","physician","nurse","front_desk"]} />}>
          <Route element={<RequireClinicalMfa />}>
          <Route path="patients/:id" element={<PatientChartLayout />}>
            <Route index element={<PatientDetail />} />
            <Route path="encounters" element={<PatientEncounters />} />
            <Route path="allergies" element={<PatientAllergies />} />
            <Route path="conditions" element={<PatientConditions />} />
            <Route path="medications" element={<PatientMedications />} />
            <Route path="prescriptions" element={<PatientPrescriptions />} />
            <Route element={<RequireRouteAccess allowedRoles={[...PRESCRIBING_ROLES]} />}>
              <Route path="new-rx" element={<NewRxPage />} />
              <Route path="new-rx/:rxId" element={<NewRxPage />} />
            </Route>
            <Route path="vitals" element={<PatientVitals />} />
            <Route path="documents" element={<PatientDocuments />} />
            <Route path="immunizations" element={<PatientImmunizations />} />
            <Route path="devices" element={<PatientImplantableDevices />} />
            <Route path="demographics" element={<PatientDemographics />} />
            <Route path="social-history" element={<PatientSocialHistory />} />
            <Route path="care-plans" element={<PatientCarePlans />} />
            <Route path="care-plans/:planId" element={<PatientCarePlanDetail />} />
            <Route path="care-team" element={<PatientCareTeam />} />
            <Route path="recall" element={<PatientRecall />} />
            <Route path="billing" element={<PatientBilling />} />
            <Route path="settings" element={<PatientSettings />} />
          </Route>
          </Route>
          </Route>

          {/* Messages - standalone chat (no clinical sidebar chrome) */}
          <Route element={<MessagesStandaloneLayout />}>
            <Route path="messages/chat" element={<ChatPage />} />
            <Route path="prp/messages/chat" element={PRP_ENABLED ? <ChatPage /> : <Navigate to="/messages/chat" replace />} />
          </Route>
        </Route>

        {/* Telehealth - full viewport outside practice shell; video opens in new tab.
            PRP counselors (primary role staff) run telehealth PRP sessions too. */}
        <Route element={<RequireRouteAccess allowedRoles={["super_admin","org_admin","provider","physician","nurse","front_desk","staff","prp_rc","prp_director"]} />}>
          <Route element={<TelehealthStandaloneLayout />}>
            <Route path="telehealth/:sessionId" element={<TelehealthSession />} />
            <Route path="telehealth/appointment/:appointmentId" element={<TelehealthAppointment />} />
          </Route>
        </Route>

        {/* ── Public Website Studio (Fullscreen Side-by-Side Visual Builder) ── */}
        <Route element={<RequireRouteAccess allowedRoles={["super_admin","org_admin"]} />}>
          <Route path="settings/public-site/studio" element={<SettingsPublicSiteStudio />} />
          <Route path="website-studio" element={<SettingsPublicSiteStudio />} />
        </Route>
        </Route>{/* </PrpOnlyGuard> */}
        </Route>{/* </RequireAuth> */}
      </Routes>
    </Suspense>
  );
}
